import { cn } from "@/lib/utils";
import { BadgeCheck, Crown, Gem } from "lucide-react";
import { topBadge, badgeMeta } from "@/lib/badges";

const SIZES = {
  xs: "h-8 w-8 text-[11px] rounded-xl",
  sm: "h-10 w-10 text-xs rounded-2xl",
  md: "h-12 w-12 text-sm rounded-2xl",
  lg: "h-16 w-16 text-lg rounded-[1.25rem]",
  xl: "h-24 w-24 text-3xl rounded-[1.75rem]",
};

// Rounded-square gradient avatar with initials. When `flair` is on (default), premium members get
// a glowing ring and verified members get a check badge — a lightweight social-proof cue that shows
// anywhere the avatar renders (chat list, chat header, etc.). Free users look exactly as before.
export function Avatar({ user, size = "md", emoji, className = "", flair = true }) {
  const colors = user?.avatar_colors || ["#01FABD", "#3591F4"];
  const initials = user?.initials || user?.display_name?.slice(0, 2)?.toUpperCase() || "?";
  const isBloqPro = flair && (user?.plan === "circle_pro" || user?.subscription?.plan === "circle_pro");
  const isPremium = flair && !!user?.premium;
  const isVerified = flair && !!user?.verified;
  const isOrg = flair && !!user?.verified_org;
  const hasNft = flair && !!user?.nft_avatar;
  const nftImg = user?.nft_avatar?.thumbnail_url || user?.nft_avatar?.image_url || null;
  const photo = nftImg || user?.avatar_b64 || null; // NFT art wins, else the uploaded profile picture
  const showBadge = size !== "xs";
  const badgeId = showBadge && flair ? topBadge(user?.badges || []) : null;
  const BadgeM = badgeId ? badgeMeta(badgeId) : null;

  const ring = isOrg
    ? "0 0 0 2px #F5C518, 0 0 14px rgba(245,197,24,0.5)"
    : hasNft
      ? "0 0 0 2px #A855F7, 0 0 14px rgba(168,85,247,0.55)"
      : isBloqPro
        ? "0 0 0 2px #8B5CF6, 0 0 14px rgba(139,92,246,0.55)"
        : isPremium
          ? "0 0 0 2px #01FABD, 0 0 12px rgba(1,250,189,0.5)"
          : undefined;

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center font-heading font-bold text-white",
        ring ? "" : "shadow-inner",
        SIZES[size],
        className,
      )}
      style={{
        backgroundImage: `linear-gradient(135deg, ${colors[0]}, ${colors[1] || colors[0]})`,
        boxShadow: ring,
      }}
      data-testid="avatar"
    >
      {emoji ? <span className="leading-none">{emoji}</span> : initials}
      {photo && (
        <img
          src={photo}
          alt={user?.nft_avatar?.name || user?.display_name || "avatar"}
          className={cn("absolute inset-0 h-full w-full object-cover", SIZES[size].match(/rounded-\S+/)?.[0])}
          data-testid={nftImg ? "avatar-nft-img" : "avatar-photo-img"}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      {showBadge && (isVerified || isOrg) && (
        <span
          className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-bloq-bg p-[1.5px]"
          data-testid={isOrg ? "avatar-org-flair" : "avatar-verified-flair"}
          title={isOrg ? "Verified organization" : "Verified"}
        >
          <BadgeCheck className={cn("h-3.5 w-3.5", isOrg ? "text-amber-300" : "text-bloq-blue")} strokeWidth={2.5} />
        </span>
      )}
      {showBadge && isBloqPro && (
        <span
          className="absolute -right-1 -top-1 flex items-center justify-center rounded-full bg-bloq-bg p-[1.5px]"
          data-testid="avatar-pro-flair"
          title="Circle Pro"
        >
          <Crown className="h-3 w-3 text-bloq-purple" fill="currentColor" />
        </span>
      )}
      {showBadge && hasNft && (
        <span
          className="absolute -left-1 -top-1 flex items-center justify-center rounded-full bg-bloq-bg p-[1.5px]"
          data-testid="avatar-nft-flair"
          title={`NFT verified${user?.nft_avatar?.name ? ` · ${user.nft_avatar.name}` : ""}`}
        >
          <Gem className="h-3 w-3" style={{ color: "#A855F7" }} fill="currentColor" />
        </span>
      )}
      {BadgeM && (
        <span
          className="absolute -bottom-1 -left-1 flex items-center justify-center rounded-full bg-bloq-bg p-[1.5px]"
          data-testid="avatar-badge-flair"
          title={BadgeM.label}
        >
          <BadgeM.Icon className="h-3 w-3" style={{ color: BadgeM.color }} strokeWidth={2.5} />
        </span>
      )}
    </div>
  );
}
